import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, IndianRupee } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Money, PageHeader, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { addressText } from "../components/AddressFields";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";

function StatementTab({ partyId, partyName }) {
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const load = () => {
    const qs = `${frm ? `?frm=${frm}${to ? `&to=${to}` : ""}` : to ? `?to=${to}` : ""}`;
    setData(null);
    api.get(`/statements/${partyId}${qs}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [partyId]); // eslint-disable-line
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-2 no-print">
        <div><Label className="text-xs">From</Label><Input data-testid="statement-from" type="date" className="mt-1 w-40" value={frm} onChange={(e) => setFrm(e.target.value)} /></div>
        <div><Label className="text-xs">To</Label><Input data-testid="statement-to" type="date" className="mt-1 w-40" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <Button variant="outline" data-testid="statement-apply" onClick={load}>Apply</Button>
        <Button variant="outline" data-testid="statement-print" onClick={() => window.print()}>Print / PDF</Button>
      </div>
      <div className="print-area rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden" data-testid="statement-card">
        {!data ? <TableSkeleton /> : (
          <>
            <div className="border-b border-slate-100 dark:border-slate-800 p-4">
              <p className="font-display font-semibold">Statement of Account</p>
              <p className="text-xs text-slate-500">{data.company?.name} → {partyName} · Period: {data.from} to {data.to}</p>
              <div className="mt-2 flex flex-wrap gap-4 text-xs">
                <span>Opening: <Money value={data.opening_balance} className="font-semibold" /></span>
                <span>Billed (Dr): <Money value={data.summary.total_debit} className="font-semibold" /></span>
                <span>Received (Cr): <Money value={data.summary.total_credit} className="font-semibold" /></span>
                <span>Closing: <Money value={data.summary.closing_balance} className="font-bold text-red-600" data-testid="statement-closing" /></span>
              </div>
            </div>
            <table className="w-full text-sm" data-testid="statement-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">Date</th><th className="p-3">Type</th><th className="p-3">Document</th>
                <th className="p-3 text-right">Debit</th><th className="p-3 text-right">Credit</th><th className="p-3 text-right">Balance</th></tr></thead>
              <tbody>
                {data.entries.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-sm text-slate-400">No transactions in this period</td></tr>}
                {data.entries.map((e, i) => (
                  <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="p-3">{fmtDate(e.date)}</td>
                    <td className="p-3 capitalize text-slate-500">{e.type.replace(/_/g, " ")}</td>
                    <td className="p-3 font-mono text-xs">{e.doc_no}</td>
                    <td className="p-3 text-right">{e.debit ? <Money value={e.debit} /> : "—"}</td>
                    <td className="p-3 text-right">{e.credit ? <Money value={e.credit} /> : "—"}</td>
                    <td className="p-3 text-right"><Money value={e.balance} className="font-semibold" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

export default function PartyProfile() {
  const { id } = useParams();
  const { activeId } = useCompany();
  const [party, setParty] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [payments, setPayments] = useState(null);
  const [ledger, setLedger] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!activeId) return;
    setParty(null); setInvoices(null); setPayments(null); setLedger(null);
    api.get(`/parties/${id}`).then((r) => setParty(r.data)).catch((e) => { toast.error(errMsg(e)); nav("/parties"); });
    api.get(`/invoices?party_id=${id}&limit=200`).then((r) => setInvoices(r.data.items)).catch(() => setInvoices([]));
    api.get(`/payments?party_id=${id}&limit=200`).then((r) => setPayments(r.data.items)).catch(() => setPayments([]));
    api.get(`/ledger/${id}`).then((r) => setLedger(r.data)).catch(() => setLedger({ entries: [], summary: {} }));
  }, [id, activeId]); // eslint-disable-line

  if (!party) return <TableSkeleton />;

  const stats = [
    ["Total Invoiced", party.total_invoiced, ""], ["Total Paid", party.total_paid, "text-emerald-600"],
    ["Outstanding", party.outstanding, "text-red-600"], ["Customer Advance", party.advance, "text-sky-600"],
  ];

  return (
    <div data-testid="party-profile-page">
      <Button variant="ghost" size="sm" className="mb-3 -ml-2" onClick={() => nav("/parties")} data-testid="back-to-parties">
        <ArrowLeft className="mr-1 h-4 w-4" /> Parties
      </Button>
      <PageHeader title={party.name} testId="party-profile-name"
        sub={`${party.code} · ${party.type} · ${party.mobile || "no mobile"} ${party.gstin ? "· GSTIN " + party.gstin : ""}`}>
        <Button variant="outline" data-testid="party-record-payment" onClick={() => nav(`/payments?new=1&party=${id}`)}>
          <IndianRupee className="mr-1.5 h-4 w-4" /> Record Payment
        </Button>
        <Button data-testid="party-new-invoice" className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"
          onClick={() => nav(`/invoices/new?party=${id}`)}>
          <FileText className="mr-1.5 h-4 w-4" /> New Invoice
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {stats.map(([label, val, cls]) => (
          <Card key={label} className="border-slate-200/80 dark:border-slate-800">
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
              <Money value={val} className={`block mt-1 text-lg font-bold ${cls}`} />
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="invoices" data-testid="party-tabs">
        <TabsList>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
          <TabsTrigger value="statement" data-testid="tab-statement">Statement</TabsTrigger>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
            {!invoices ? <TableSkeleton /> : invoices.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400">No invoices for this party</p>
            ) : (
              <table className="w-full text-sm" data-testid="party-invoices-table">
                <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="p-3">Invoice No</th><th className="p-3">Date</th><th className="p-3">Due</th>
                  <th className="p-3 text-right">Total</th><th className="p-3 text-right">Paid</th>
                  <th className="p-3 text-right">Balance</th><th className="p-3">Status</th></tr></thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} onClick={() => nav(`/invoices/${inv.id}`)}
                      className="cursor-pointer border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                      <td className="p-3 font-mono text-xs">{inv.invoice_no}</td>
                      <td className="p-3">{fmtDate(inv.date)}</td><td className="p-3">{fmtDate(inv.due_date)}</td>
                      <td className="p-3 text-right"><Money value={inv.total} /></td>
                      <td className="p-3 text-right"><Money value={inv.paid} /></td>
                      <td className="p-3 text-right"><Money value={inv.balance} /></td>
                      <td className="p-3"><StatusBadge status={inv.payment_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
            {!payments ? <TableSkeleton /> : payments.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400">No payments recorded</p>
            ) : (
              <table className="w-full text-sm" data-testid="party-payments-table">
                <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="p-3">Payment No</th><th className="p-3">Date</th><th className="p-3">Mode</th>
                  <th className="p-3 text-right">Amount</th><th className="p-3 text-right">Allocated</th>
                  <th className="p-3 text-right">Unallocated</th></tr></thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="p-3 font-mono text-xs">{p.payment_no}</td>
                      <td className="p-3">{fmtDate(p.date)}</td><td className="p-3 capitalize">{p.mode?.replace(/_/g, " ")}</td>
                      <td className="p-3 text-right"><Money value={p.amount} /></td>
                      <td className="p-3 text-right"><Money value={p.allocated} /></td>
                      <td className="p-3 text-right">
                        <Money value={p.unallocated} className={p.unallocated > 0 ? "text-amber-600 font-semibold" : "text-slate-400"} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ledger" className="mt-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
            {!ledger ? <TableSkeleton /> : (
              <>
                <div className="flex flex-wrap gap-4 border-b border-slate-100 dark:border-slate-800 p-4 text-xs">
                  <span>Total Debit: <Money value={ledger.summary.total_debit} className="font-semibold" /></span>
                  <span>Total Credit: <Money value={ledger.summary.total_credit} className="font-semibold" /></span>
                  <span>Closing Balance: <Money value={ledger.summary.closing_balance} className="font-bold text-red-600" /></span>
                  <span>Advance: <Money value={ledger.summary.advance} className="font-semibold text-sky-600" /></span>
                </div>
                <table className="w-full text-sm" data-testid="party-ledger-table">
                  <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="p-3">Date</th><th className="p-3">Type</th><th className="p-3">Document</th>
                    <th className="p-3 text-right">Debit</th><th className="p-3 text-right">Credit</th>
                    <th className="p-3 text-right">Balance</th></tr></thead>
                  <tbody>
                    {ledger.entries.length === 0 && (
                      <tr><td colSpan={6} className="p-10 text-center text-sm text-slate-400">No ledger entries yet</td></tr>)}
                    {ledger.entries.map((e, i) => (
                      <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="p-3">{fmtDate(e.date)}</td>
                        <td className="p-3"><Badge variant="secondary" className="capitalize text-[10px]">{e.type.replace(/_/g, " ")}</Badge></td>
                        <td className="p-3 font-mono text-xs">
                          {e.type === "invoice" && e.doc_id
                            ? <button className="text-blue-600 hover:underline" onClick={() => nav(`/invoices/${e.doc_id}`)}>{e.doc_no}</button>
                            : e.doc_no}
                        </td>
                        <td className="p-3 text-right">{e.debit ? <Money value={e.debit} /> : "—"}</td>
                        <td className="p-3 text-right">{e.credit ? <Money value={e.credit} /> : "—"}</td>
                        <td className="p-3 text-right"><Money value={e.balance} className="font-semibold" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="statement" className="mt-4">
          <StatementTab partyId={id} partyName={party.name} />
        </TabsContent>

        <TabsContent value="overview" className="mt-4">
          <Card className="border-slate-200/80 dark:border-slate-800">
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 p-5 text-sm" data-testid="party-overview">
              {[
                ["Legal Name", party.legal_name], ["Contact Person", party.contact_person],
                ["Email", party.email], ["Mobile", party.mobile],
                ["GSTIN", party.gstin], ["PAN", party.pan],
                ["Billing Address", addressText(party.billing_address_details) || party.billing_address], ["Shipping Address", addressText(party.shipping_address_details) || party.shipping_address], ["City / State", [party.city, party.state].filter(Boolean).join(", ")],
                ["Credit Limit", party.credit_limit ? `₹${party.credit_limit.toLocaleString("en-IN")}` : "—"],
                ["Credit Days", party.credit_days || "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-50 dark:border-slate-800/60 pb-2">
                  <span className="text-slate-500">{k}</span>
                  <span className="font-medium text-right">{v || "—"}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
